import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, AlertTriangle, Trash2, Check, Inbox } from "lucide-react";
import { toast } from "sonner";
import { containersApi, formatError } from "../lib/api";
import { formatDate, isExpired } from "../lib/format";

export const RisksOverlay = ({ open, onClose, onChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const list = await containersApi.list({ status: "fresh", sort: "expiring_soon" });
      setItems(list.filter(isExpired));
    } catch (err) {
      console.warn("Failed to load risks:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const discard = async (id) => {
    setBusyId(id);
    try {
      await containersApi.update(id, { status: "discarded" });
      setItems((prev) => prev.filter((c) => c.id !== id));
      toast.success("Marked discarded — container is now Unused");
      onChange?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusyId(null);
    }
  };

  const count = items.length;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-md flex items-end sm:items-center justify-center"
          data-testid="risks-overlay-backdrop"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[480px] bg-[#FDFBF7] rounded-t-3xl sm:rounded-3xl p-6 sm:m-4 max-h-[88vh] overflow-y-auto"
            data-testid="risks-overlay"
          >
            <div className="flex items-start justify-between gap-3 mb-1">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#FBE2DA] flex items-center justify-center">
                  <AlertTriangle
                    className="w-5 h-5 text-[#8C2A0F] caution-anim"
                    strokeWidth={2.6}
                  />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8C2A0F]">
                    Risks
                  </div>
                  <div className="font-heading text-xl font-bold text-[#2D3142]">
                    Past best-by
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="risks-overlay-close"
                aria-label="Close"
                className="w-9 h-9 rounded-full hover:bg-[#F5F3ED] flex items-center justify-center text-[#4F5D75]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-[#4F5D75] mt-2 leading-relaxed">
              These containers are past their best-by date. Tap "Discarded" to empty the container — it'll move to your Unused count so the Q-Box is ready to reuse.
            </p>

            {loading ? (
              <div className="mt-6 tup-card p-6 text-center text-sm text-[#8A93A6]">
                Loading…
              </div>
            ) : count === 0 ? (
              <div
                className="mt-6 tup-card p-6 text-center"
                data-testid="risks-overlay-empty"
              >
                <Inbox className="w-7 h-7 mx-auto text-[#8A93A6]" />
                <div className="font-heading text-base font-semibold text-[#2D3142] mt-3">
                  All clear
                </div>
                <p className="text-xs text-[#4F5D75] mt-2 leading-relaxed">
                  Nothing is past its best-by date right now.
                </p>
              </div>
            ) : (
              <ul
                className="mt-5 space-y-3"
                data-testid="risks-overlay-list"
              >
                {items.map((c) => {
                  const days = Math.abs(c.days_until_expiry || 0);
                  const ago = days === 1 ? "yesterday" : `${days} days ago`;
                  return (
                    <li
                      key={c.id}
                      className="tup-card-expired rounded-2xl p-4 border-l-4 border-[#D95D39]"
                      data-testid={`risk-item-${c.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <AlertTriangle
                          className="w-5 h-5 text-[#8C2A0F] caution-anim shrink-0 mt-0.5"
                          strokeWidth={2.6}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-heading text-base font-semibold text-[#8C2A0F] truncate">
                            {c.name}
                          </div>
                          <div className="text-[11px] text-[#8C2A0F]/80 mt-0.5">
                            Best-by {formatDate(c.expiration_date)} · {ago}
                          </div>
                          {c.notes ? (
                            <div className="text-xs text-[#4F5D75] mt-1 line-clamp-1">
                              {c.notes}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => discard(c.id)}
                        disabled={busyId === c.id}
                        data-testid={`risk-discard-btn-${c.id}`}
                        className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-white border-2 border-[#D95D39] text-[#8C2A0F] font-semibold text-sm py-2.5 hover:bg-[#FBEAE5] transition-colors disabled:opacity-60"
                      >
                        {busyId === c.id ? (
                          <>
                            <Check className="w-4 h-4" /> Marking…
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4" /> Discarded
                          </>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
